import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import NotificationReaders from './NotificationReaders';

const AdminAnnouncements = () => {
  const navigate = useNavigate();

  // === Core Data States ===
  const [announcements, setAnnouncements] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');

  // === UI States ===
  const [activeTab, setActiveTab] = useState('published');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [receiptReport, setReceiptReport] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [targetingError, setTargetingError] = useState('');

  // === Statistics ===
  const [stats, setStats] = useState({
    totalAnnouncements: 0,
    publishedCount: 0,
    draftCount: 0,
    totalViews: 0
  });

  // === Form States ===
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    targetType: 'all',
    targetIds: [],
    priority: 'normal',
    category: 'general',
    status: 'draft',
    publishDate: '',
    expiryDate: ''
  });

  // ========================= INITIALIZATION =========================
  useEffect(() => {
    loadAllData();
    const timer = setInterval(async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/announcements`, { credentials: 'include' });
        if (response.ok) setAnnouncements(await response.json());
      } catch (error) { console.error('Unable to refresh announcement counts:', error); }
    }, 15000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    calculateStats();
  }, [announcements]);

  // ========================= DATA FETCHING =========================
  const loadAllData = async () => {
    setLoading(true);
    try {
      const [announcementsResult, studentsResult, classesResult] = await Promise.allSettled([
        fetch(API_ENDPOINTS.ANNOUNCEMENTS.replace('/api/announcements', '/api/admin/announcements'), { credentials: 'include' }),
        fetch(API_ENDPOINTS.STUDENTS, { credentials: 'include' }),
        fetch(API_ENDPOINTS.CLASSES)
      ]);
      if (announcementsResult.status === 'rejected') throw new Error('Unable to reach school announcements. Please try again.');
      const announcementsRes = announcementsResult.value;
      if (announcementsRes.status === 401) {
        navigate('/admin?returnTo=%2Fadmin%2Fnews&reason=session-expired', { replace: true });
        return;
      }
      if (!announcementsRes.ok) {
        const failure = await announcementsRes.json().catch(() => ({}));
        throw new Error(announcementsRes.status === 403 ? 'This account cannot manage announcements. Use an administrator account.' : failure.error || 'Unable to load announcements. Please try again.');
      }

      const announcementsData = await announcementsRes.json();
      setAnnouncements(announcementsData);
      setLoadError('');
      const targeting = await Promise.allSettled([studentsResult, classesResult].map(async result => {
        if (result.status !== 'fulfilled' || !result.value.ok) throw new Error('Targeting unavailable');
        const items = await result.value.json();
        if (!Array.isArray(items)) throw new Error('Invalid targeting response');
        return items;
      }));
      if (targeting[0].status === 'fulfilled') setAllStudents(targeting[0].value);
      if (targeting[1].status === 'fulfilled') setAllClasses(targeting[1].value);
      setTargetingError(targeting.some(result => result.status === 'rejected') ? 'Student or class targeting is temporarily unavailable. Your announcements are still available.' : '');
    } catch (error) {
      console.error('Error loading data:', error);
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const published = announcements.filter(a => a.status === 'published').length;
    const drafts = announcements.filter(a => a.status === 'draft').length;
    const totalViews = announcements.reduce((sum, a) => sum + Number(a.views || 0), 0);

    setStats({
      totalAnnouncements: announcements.length,
      publishedCount: published,
      draftCount: drafts,
      totalViews
    });
  };

  // ========================= FORM HANDLING =========================
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTargetIdsChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      targetIds: checked
        ? [...prev.targetIds, value]
        : prev.targetIds.filter(id => id !== value)
    }));
  };

  const openCreateModal = () => {
    setFormData({
      title: '',
      content: '',
      targetType: 'all',
      targetIds: [],
      priority: 'normal',
      category: 'general',
      status: 'draft',
      publishDate: '',
      expiryDate: ''
    });
    setShowCreateModal(true);
  };

  const openEditModal = (announcement) => {
    setFormData({
      title: announcement.title || '',
      content: announcement.content || '',
      targetType: announcement.targetType || 'all',
      targetIds: announcement.targetIds || [],
      priority: announcement.priority || 'normal',
      category: announcement.category || 'general',
      status: announcement.status || 'draft',
      publishDate: announcement.publishDate ? announcement.publishDate.substring(0, 16) : '',
      expiryDate: announcement.expiryDate ? announcement.expiryDate.substring(0, 16) : ''
    });
    setEditingAnnouncement(announcement);
    setShowEditModal(true);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.content.trim()) errors.content = 'Content is required';
    if (formData.targetType !== 'all' && formData.targetIds.length === 0) {
      errors.targetIds = 'Please select at least one target';
    }

    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      alert('Please fix the errors in the form');
      return;
    }

    try {
      const announcementData = {
        ...formData,
        author: 'System Administrator', // In a real app, this would come from auth
        publishDate: formData.publishDate || new Date().toISOString(),
        expiryDate: formData.expiryDate || null
      };

      let response;
      if (editingAnnouncement) {
        response = await fetch(`${API_ENDPOINTS.ANNOUNCEMENTS}/${editingAnnouncement.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(announcementData)
        });
      } else {
        response = await fetch(API_ENDPOINTS.ANNOUNCEMENTS, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(announcementData)
        });
      }

      if (response.ok) {
        setShowCreateModal(false);
        setShowEditModal(false);
        setEditingAnnouncement(null);
        loadAllData();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to save announcement');
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert('Failed to save announcement. Please try again.');
    }
  };

  const handleDelete = async (announcement) => {
    if (!window.confirm(`Are you sure you want to delete "${announcement.title}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.ANNOUNCEMENTS}/${announcement.id}`, {
        method: 'DELETE', credentials: 'include'
      });

      if (response.ok) {
        loadAllData();
      } else {
        alert('Failed to delete announcement');
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Failed to delete announcement. Please try again.');
    }
  };

  const handleStatusChange = async (announcement, newStatus) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.ANNOUNCEMENTS}/${announcement.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        loadAllData();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // ========================= FILTERING =========================
  const getFilteredAnnouncements = () => {
    switch (activeTab) {
      case 'published':
        return announcements.filter(a => a.status === 'published');
      case 'drafts':
        return announcements.filter(a => a.status === 'draft');
      case 'scheduled':
        return announcements.filter(a => a.status === 'scheduled');
      default:
        return announcements;
    }
  };

  // ========================= HELPERS =========================
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTargetDescription = (announcement) => {
    if (announcement.targetType === 'all') return 'All Students & Parents';
    if (announcement.targetType === 'parents') return 'Parents Only';
    if (announcement.targetType === 'class') return `Classes: ${announcement.targetIds.join(', ')}`;
    if (announcement.targetType === 'individual') return `${announcement.targetIds.length} Individual Students`;
    return 'Unknown';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading announcements...</div>
      </div>
    );
  }

  const filteredAnnouncements = getFilteredAnnouncements();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-3 py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">School Announcements</h1>
              <p className="text-sm text-gray-600">Manage news and updates for students and parents</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">📢</div>
            <h3 className="text-lg font-semibold text-gray-800">{stats.totalAnnouncements}</h3>
            <p className="text-sm text-gray-600">Total Announcements</p>
          </div>

          <div className="bg-green-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Share</div>
            <h3 className="text-lg font-semibold text-green-800">{stats.publishedCount}</h3>
            <p className="text-sm text-green-600">Published</p>
          </div>

          <div className="bg-yellow-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Note</div>
            <h3 className="text-lg font-semibold text-yellow-800">{stats.draftCount}</h3>
            <p className="text-sm text-yellow-600">Drafts</p>
          </div>

          <div className="bg-blue-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">👁️</div>
            <h3 className="text-lg font-semibold text-blue-800">{stats.totalViews}</h3>
            <p className="text-sm text-blue-600">Total Views</p>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex flex-wrap justify-between items-center gap-3">
            {/* Tabs */}
            <div className="flex flex-wrap gap-1">
              {[
                { key: 'published', label: 'Published', count: stats.publishedCount },
                { key: 'drafts', label: 'Drafts', count: stats.draftCount },
                { key: 'all', label: 'All', count: stats.totalAnnouncements }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-md font-medium ${
                    activeTab === tab.key
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            <button onClick={() => setReceiptReport({ type: 'progress' })} className="border border-slate-300 text-slate-800 px-4 py-2 rounded">Progress Notification Views</button>
            {/* Create Button */}
            <button
              onClick={openCreateModal}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200 flex items-center space-x-2"
            >
              <span>+</span>
              <span>Create Announcement</span>
            </button>
          </div>
        </div>

        {/* Announcements List */}
        {loadError && <p role="alert" className="text-red-700 mb-4">{loadError}</p>}
        {targetingError && <p role="status" className="text-amber-800 mb-4">{targetingError}</p>}
        {receiptReport && <NotificationReaders {...receiptReport} onClose={() => setReceiptReport(null)} />}
        <div className="space-y-4">
          {filteredAnnouncements.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="text-6xl mb-4">Inbox</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Announcements Found</h3>
              <p className="text-gray-600 mb-6">
                {activeTab === 'published'
                  ? 'No published announcements yet.'
                  : activeTab === 'drafts'
                  ? 'No draft announcements.'
                  : 'Create your first announcement to get started.'}
              </p>
              {activeTab !== 'published' && (
                <button
                  onClick={openCreateModal}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                >
                  Create First Announcement
                </button>
              )}
            </div>
          ) : (
            filteredAnnouncements.map((announcement) => (
              <div key={announcement.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:flex-wrap justify-between items-start gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900 break-words">{announcement.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(announcement.priority)}`}>
                          {announcement.priority}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(announcement.status)}`}>
                          {announcement.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        By {announcement.author} ? {new Date(announcement.createdAt).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2">
                      {announcement.status === 'draft' && (
                        <button
                          onClick={() => handleStatusChange(announcement, 'published')}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                        >
                          Publish
                        </button>
                      )}
                      {announcement.status === 'published' && (
                        <button
                          onClick={() => handleStatusChange(announcement, 'draft')}
                          className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                        >
                          Unpublish
                        </button>
                      )}
                      <button
                        onClick={() => openEditModal(announcement)}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(announcement)}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Content Preview */}
                  <div className="mb-4">
                    <p className="text-gray-700 line-clamp-3">{announcement.content}</p>
                  </div>

                  {/* Metadata */}
                  <div className="flex flex-wrap justify-between items-center gap-3 text-sm text-gray-600">
                    <div className="flex flex-wrap items-center gap-4">
                      <span>Target: {getTargetDescription(announcement)}</span>
                      <span>Category: {announcement.category}</span>
                      <span>Views: {announcement.views || 0}</span>
                      <span>Parents: {announcement.parentViews || 0}</span>
                      <span>Acknowledged: {announcement.acknowledgements || 0}</span>
                    </div>
                    <div className="text-right">
                      {announcement.publishDate && (
                        <div>Published: {new Date(announcement.publishDate).toLocaleDateString()}</div>
                      )}
                      {announcement.expiryDate && (
                        <div>Expires: {new Date(announcement.expiryDate).toLocaleDateString()}</div>
                      )}
                    </div>
                  </div>
                  <button onClick={() => setReceiptReport({ id: announcement.id, type: 'announcement' })} className="mt-4 border border-slate-300 text-slate-800 px-3 py-2 rounded text-sm">Parent Confirmations</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {showCreateModal ? 'Create New Announcement' : 'Edit Announcement'}
              </h3>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Note Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter announcement title"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="content"
                        value={formData.content}
                        onChange={handleFormChange}
                        rows="6"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter announcement content..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="general">General</option>
                        <option value="academic">Academic</option>
                        <option value="events">Events</option>
                        <option value="sports">Sports</option>
                        <option value="emergency">Emergency</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Targeting */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Goals Target Audience</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Type
                      </label>
                      <select
                        name="targetType"
                        value={formData.targetType}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Students & Parents</option>
                        <option value="parents">Parents Only</option>
                        <option value="class">Specific Classes</option>
                        <option value="individual">Individual Students</option>
                      </select>
                    </div>

                    {formData.targetType === 'class' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Classes
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto border rounded-md p-3 bg-gray-50">
                          {allClasses.length === 0 ? (
                            <p className="text-sm text-gray-500 col-span-full">No classes available</p>
                          ) : (
                            allClasses.map(cls => (
                              <label key={cls} className="flex items-center space-x-2 bg-white p-2 rounded border">
                                <input
                                  type="checkbox"
                                  value={cls}
                                  checked={formData.targetIds.includes(cls)}
                                  onChange={handleTargetIdsChange}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium">{cls}</span>
                              </label>
                            ))
                          )}
                        </div>
                        {allClasses.length > 0 && (
                          <p className="text-xs text-gray-600 mt-2">
                            {formData.targetIds.length} of {allClasses.length} classes selected
                          </p>
                        )}
                      </div>
                    )}

                    {formData.targetType === 'individual' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Students
                        </label>

                        {/* Search Box for Students */}
                        <input
                          type="text"
                          placeholder="Search by name or username..."
                          value={studentSearchQuery}
                          onChange={(e) => setStudentSearchQuery(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                        />

                        <div className="max-h-48 overflow-y-auto border rounded-md p-3">
                          {allStudents.length === 0 ? (
                            <p className="text-sm text-gray-500">No students available</p>
                          ) : (
                            allStudents
                              .filter(student =>
                                studentSearchQuery === '' ||
                                student.firstName.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
                                student.lastName.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
                                student.username.toLowerCase().includes(studentSearchQuery.toLowerCase())
                              )
                              .slice(0, 50) // Limit to 50 for performance
                              .map(student => (
                                <label key={student.username} className="flex items-center space-x-2 py-1">
                                  <input
                                    type="checkbox"
                                    value={student.username}
                                    checked={formData.targetIds.includes(student.username)}
                                    onChange={handleTargetIdsChange}
                                    className="rounded text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-sm">
                                    {student.firstName} {student.lastName} ({student.username}) - {student.className}
                                  </span>
                                </label>
                              ))
                          )}
                        </div>

                        {formData.targetIds.length > 0 && (
                          <p className="text-xs text-gray-600 mt-2">
                            {formData.targetIds.length} student(s) selected
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Publishing Settings */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">📅 Publishing Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="scheduled">Scheduled</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Publish Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        name="publishDate"
                        value={formData.publishDate}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiry Date & Time (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
                    </div>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleSave}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    {showCreateModal ? 'Create Announcement' : 'Update Announcement'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setEditingAnnouncement(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncements;



